<template>
  <div
    v-if="display"
    class="gift-with-purchase-trigger critical-component-clear"
    :class="{ 'gift-with-purchase-trigger--cart': cart }"
  >
    <progress-bar
      v-if="messageString && progressToLargest && cart"
      :percentage="progressToLargest * 100"
      :progress-text="messageString"
    >
      <icon-gift />
    </progress-bar>

    <div
      v-else-if="messageString && product"
      class="gift-with-purchase-trigger__product"
    >
      <icon-gift />
      <span
        class="text-body-2xs gift-with-purchase-trigger__product-message"
        v-text="messageString"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Gift with purchase trigger (gift-with-purchase-trigger)
 * -----------------------------------------------------------------------------
 * Dynamic text and trigger to show modal.
 * - Displayed on PDP products that have associated gift and in the cart drawer
 *   and on the cart page.
 * - PDP displays gift message.
 * - Cart displays progress text, available gifts trigger, and progress bar.
 * - Cart progress text updates to all gifts added when thresholds met and all
 *   available gifts have been added.
 *
 * @param {Boolean} [cart] - Being rendered in the cart.
 * @param {Object} [product] - Product object if being rendered on product page.
 *
 */
import { mapGetters, mapState } from 'vuex'

import IconGift from '~icons/misc/gift.svg'

import ProgressBar from '~async/progress-bar/progress-bar'

export default {
  name: 'GiftWithPurchaseTrigger',

  components: {
    IconGift,
    ProgressBar,
  },

  props: {
    cart: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getApplicableCart: 'gift-with-purchase/getApplicableCart',
      getProductsWithGift: 'gift-with-purchase/getProductsWithGift',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      addedPromotions: (state) => state['gift-with-purchase'].added,
      availablePromotions: (state) => state['gift-with-purchase'].available,
      pendingPromotions: (state) => state['gift-with-purchase'].pending,
      enablePromotions: (state) => state['gift-with-purchase'].settings.enable,
      minimumPrice: (state) => state['gift-with-purchase'].settings.minimumPrice,
      promotions: (state) => state['gift-with-purchase'].promotions,
    }),

    /**
     * Compute if the trigger should be displayed.
     * @returns {Boolean}
     */
    display() {
      if (!this.enablePromotions) {
        return false
      }

      return Boolean(this.thresholds.length || this.availablePromotions.length || this.pendingPromotions.length)
    },

    /**
     * Compute gifts available text.
     * @returns {String}
     */
    giftsAvailableText() {
      const free = this.availablePromotions.every((promotion) => {
        return promotion.action.discount === 100 && !this.minimumPrice
      })

      const config = {
        pluralise: this.availablePromotions.length,
        replace: {
          quantity: this.availablePromotions.length,
        },
      }

      const text = free
        ? this.$string('gift_with_purchase.title.free', config)
        : this.$string('gift_with_purchase.title.gift', config)

      return `${text} - ${this.$string('gift_with_purchase.view_options')}`
    },

    /**
     * Compute largest threshold.
     * @returns {Object}
     */
    largestThreshold() {
      return [...this.thresholds].reverse()[0]
    },

    /**
     * Compute message string.
     * @returns {String}
     */
    messageString() {
      if (this.product) {
        const handle = this.getProductsWithGift[this.product.handle]

        if (handle) {
          return handle.isFree
            ? this.$string('gift_with_purchase.product.free')
            : this.$string('gift_with_purchase.product.cost')
        }

        return ''
      }

      /**
       * If no thresholds.
       */
      if (!this.thresholds.length) {
        return ''
      }

      const free = this.addedPromotions.every((promotion) => {
          return promotion.action.discount === 100 && !this.minimumPrice
        })

      /**
       * If all thresholds met and all gifts added.
       */
      if (this.progressToLargest === 1 && !this.availablePromotions.length) {
        return free
          ? this.$string('gift_with_purchase.complete.free')
          : this.$string('gift_with_purchase.complete.cost')
      }

      /**
       * If all thresholds met and all gifts not yet added.
       */
      if (!this.nextThreshold && this.thresholds.length) {
        if (this.progressDisplay === 1) {
          return free ? this.$string('gift_with_purchase.qualified.free.single') :
          this.$string('gift_with_purchase.qualified.cost.single')
        }

        return free ? this.$string('gift_with_purchase.qualified.free.multiple', {
          replace: {
            number: this.progressDisplay,
          },
        }) : this.$string('gift_with_purchase.qualified.cost.multiple', {
          replace: {
            number: this.progressDisplay,
          },
        })
      }

      /**
       * Determine cost to next threshold and whether the gift is free.
       */
      const costToNextThreshold =
        this.nextThreshold.trigger.value[cnvs.store.currency] -
        this.getApplicableCart.total_price

      const nextFree =
        this.nextThreshold.action.discount === 100 &&
        !this.minimumPrice

      /**
       * Determine if before first threshold.
       */
      const nextThresholdIndex = this.thresholds.findIndex((threshold) => {
        return threshold === this.nextThreshold
      })

      const nextThresholdValue = this.nextThreshold.trigger.value[cnvs.store.currency]
      let threshold = nextThresholdValue

      /**
       * If not first threshold then get previous threshold to determine
       * difference for progress text.
       */
      if (nextThresholdIndex > 0) {
        const previousThreshold = this.thresholds[nextThresholdIndex - 1]

        threshold =
          nextThresholdValue -
          previousThreshold.trigger.value[cnvs.store.currency]

      }

      const config = {
        replace: {
          remaining: this.$formatMoney(costToNextThreshold),
          threshold: this.$formatMoney(threshold),
        },
      }

      if (nextFree) {
        return nextThresholdIndex > 0
          ? this.$string('gift_with_purchase.threshold.free.multi_progress', config)
          : this.$string('gift_with_purchase.threshold.free.progress', config)
      }

      return nextThresholdIndex > 0
        ? this.$string('gift_with_purchase.threshold.cost.multi_progress', config)
        : this.$string('gift_with_purchase.threshold.cost.progress', config)
    },

    /**
     * Compute next threshold.
     * - Returns false if no thresholds remaining.
     * @returns {Object|Boolean}
     */
    nextThreshold() {
      const thresholdsNotMet = this.thresholds.filter((threshold) => {
        return threshold.trigger.value[cnvs.store.currency] > this.getApplicableCart.total_price
      })

      return thresholdsNotMet.length ? thresholdsNotMet[0] : false
    },

    /**
     * Compute whether to display progress bar.
     * @returns {Boolean}
     */
    progressDisplay() {
      return (
        this.cart &&
        this.thresholds.length &&
        (this.availablePromotions.length || this.progressToLargest < 1)
      )
    },

    /**
     * Compute progress decimal to next threshold.
     * - Can't return a value larger than 1.
     * @returns {Number}
     */
    progressToNext() {
      if (!this.nextThreshold) {
        return 0
      }

      const nextThreshold = this.nextThreshold.trigger.value[cnvs.store.currency]
      return Math.min(this.getApplicableCart.total_price / nextThreshold, 1)
    },

    /**
     * Compute progress decimal to largest threshold.
     * - Can't return a value larger than 1.
     * @returns {Number}
     */
    progressToLargest() {
      if (!this.largestThreshold) {
        return 0
      }

      const largestThresholdValue = this.largestThreshold.trigger.value[cnvs.store.currency]
      return Math.min(this.getApplicableCart.total_price / largestThresholdValue, 1)
    },

    /**
     * Compute all thresholds in order.
     * @returns {Array}
     */
    thresholds() {
      let thresholds = this.promotions.filter((promotion) => {
        return promotion.trigger.type === 'threshold'
      })

      thresholds = thresholds.sort((previous, current) => {
        return (
          previous.trigger.value[cnvs.store.currency] -
          current.trigger.value[cnvs.store.currency]
        )
      })

      return thresholds
    },
  },
}
</script>

<style lang="scss">
@import './gift-with-purchase-trigger';
</style>
